import type { DashboardTeamMember } from '../../types/dashboard';
import { Avatar, Badge, ProgressBar } from '../ui';

interface TeamPanelProps {
  members: DashboardTeamMember[];
  loading?: boolean;
}

export function TeamPanel({ members, loading }: TeamPanelProps) {
  // Derive initials from name: first letter of each word, joined, uppercase, max 2 chars
  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  // Determine bar color based on booked value
  const getBarColor = (booked: number) => (booked > 0 ? 'bg-ink-2' : 'bg-canvas-2');

  // Determine if member is open (booked === 0 && capacity > 0)
  const isOpen = (booked: number, capacity: number) => booked === 0 && capacity > 0;

  // Calculate progress value
  const getProgressValue = (booked: number, capacity: number) =>
    capacity > 0 ? booked / capacity : 0;

  // Loading skeleton rows
  const renderSkeleton = () => (
    <div className="p-4 space-y-3.5">
      {[0, 1].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-canvas-2 rounded" />
              <div className="w-24 h-3 bg-canvas-2 rounded" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-12 h-5 bg-canvas-2 rounded" />
              <div className="w-16 h-3 bg-canvas-2 rounded" />
            </div>
          </div>
          <div className="h-1.5 bg-canvas-2 rounded-full overflow-hidden">
            <div className="h-full w-2/3 bg-canvas-2 rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  // Empty state
  if (members.length === 0 && !loading) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 hair-b flex items-center justify-between">
          <h3 className="text-[14px] font-semibold">Team today</h3>
          <span className="text-[11px] text-ink-4">0 providers</span>
        </div>
        {/* Empty body */}
        <div className="py-16 flex flex-col items-center justify-center">
          <div className="text-[15px] font-medium text-ink-3">
            No availability today
          </div>
          <div className="text-[13px] text-ink-4 mt-1 text-center max-w-sm">
            Provider schedules will appear here once availability is set.
          </div>
        </div>
      </div>
    );
  }

  // Loading state with skeleton
  if (loading && members.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 hair-b flex items-center justify-between">
          <h3 className="text-[14px] font-semibold">Team today</h3>
          <span className="h-3 w-16 bg-canvas-2 animate-pulse rounded" />
        </div>
        {renderSkeleton()}
      </div>
    );
  }

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b flex items-center justify-between">
        <h3 className="text-[14px] font-semibold">Team today</h3>
        <span className="text-[11px] text-ink-4">
          {members.length} provider{members.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Provider list */}
      <div className="p-4 space-y-3.5">
        {members.map((member) => (
          <div key={member.providerId}>
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2">
                <Avatar initials={getInitials(member.name)} size="sm" />
                <span className="text-[12.5px] font-medium">
                  {member.name}
                </span>
              </span>
              <span className="flex items-center gap-2">
                {isOpen(member.booked, member.capacity) && (
                  <Badge variant="sage">Open</Badge>
                )}
                <span className="text-[11.5px] text-ink-3 num">
                  {member.booked}{' '}
                  <span className="text-ink-4">/</span> {member.capacity}
                  <span className="text-ink-5 text-[10px] ml-0.5">slots</span>
                </span>
              </span>
            </div>
            <ProgressBar
              value={getProgressValue(member.booked, member.capacity)}
              color={getBarColor(member.booked)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
